'use client';

import { useEffect, useRef } from 'react';
import { isLighthouseClient } from '@/lib/isLighthouse';
import { pushTaboola, scheduleTaboolaFlush } from './TaboolaWidget';

// Right-rail placement shown after the "விடியோக்கள்" (Videos) section in the
// sticky sidebar. Each scrolled-in article is a new "page", so the container id
// must be unique across the session — we append the article's index to the base.
const PLACEMENT = {
  mode: 'thumbnails-rr',
  base: 'taboola-infinite-right-rail-thumbnails',
  placement: 'Infinite Right Rail Thumbnails'
} as const;
interface Props {
  /** Stable per-article identifier — makes each page's container id unique.
   *  A plain incrementing index for the legacy infinite-scroll list, or a
   *  story id/slug for the dynamic article-template renderer (any value is
   *  fine — it's only ever interpolated into a container id string). */
  pageIndex: number | string;
  /** Canonical (non-AMP) URL of this article, passed to Taboola as article:auto context. */
  canonicalUrl: string;
}

/**
 * Renders the Taboola right-rail placement for one article and runs the
 * onboarding sequence whenever the article changes: push the widget (with
 * its unique container), then re-declare `{article:'auto', url}` for this
 * page's canonical URL, then flush. Flush is debounced in TaboolaWidget so a
 * burst of articles scrolling in coalesces into one flush.
 *
 * Tracks the LAST canonicalUrl pushed (not just "ever pushed") so this keeps
 * working even if React reuses this component instance across a client-side
 * navigation instead of unmounting it — the `key={story.slug}` at the call
 * site is meant to force a fresh instance per article, but re-firing off the
 * dependency change here too means correctness doesn't hinge on that
 * remount actually happening.
 */
export default function TaboolaRightRail({
  pageIndex,
  canonicalUrl
}: Props) {
  const pushedForUrlRef = useRef<string | null>(null);
  const container = `${PLACEMENT.base}-${pageIndex}`;
  useEffect(() => {
    if (pushedForUrlRef.current === canonicalUrl || isLighthouseClient()) return;
    pushedForUrlRef.current = canonicalUrl;
    pushTaboola({
      mode: PLACEMENT.mode,
      container,
      placement: PLACEMENT.placement,
      target_type: 'mix'
    });
    pushTaboola({
      article: 'auto',
      url: canonicalUrl
    });
    scheduleTaboolaFlush();
    // container is derived from pageIndex, which changes in lockstep with canonicalUrl.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalUrl]);
  return <div id={container} className="mt-4 px-2 pb-2" />;
}